import React, { useContext, useState } from "react";
import { Spinner } from "react-bootstrap";
import {
  FaSearch, FaSyncAlt, FaClipboardList, FaEye, FaTimes, FaPlay,
  FaCheckCircle, FaClock, FaSpinner as FaSpinnerIcon, FaExclamationCircle,
} from "react-icons/fa";

import { AuthContext } from "../../context/AuthContext";
// Technician design system (same sheet the Ticket Dashboard uses) -- every
// `tech-*` hook below is scoped under the `.tech-ds` page root.
import "../technicians/TechnicianDesignSystem.css";
import "./RoleReviewFormsPage.css";

import useReviewForms, {
  getReviewStatusInfo,
  getRecipientAction,
  RECIPIENT_ACTION_REASON_LABELS,
} from "../../hooks/useReviewForms";

import FormAssignmentSummaryCards from "../../components/forms/FormAssignmentSummaryCards";
import TicketFeedbackFormsTab from "./TicketFeedbackFormsTab";
import { ELLIPSIS, getCompactPageRange } from "../../components/ui/compactPagination";

// Icon shown alongside the status label -- purely decorative (the visible
// text is still the accessible name); the tone -> icon mapping mirrors the
// tone -> color mapping already returned by getReviewStatusInfo (shared
// business logic, untouched) so it never drifts from the actual status.
const STATUS_ICONS = {
  success: FaCheckCircle,
  secondary: FaClock,
  info: FaSpinnerIcon,
  danger: FaExclamationCircle,
  light: FaClock,
};

// Tab 1 (Assigned Forms) is this page's original, unchanged content --
// forms this user personally must Start/Continue/Save Draft/Submit,
// sourced from form_assignment_users.user_id = them (useReviewForms below).
// Tab 2 (Ticket Feedback Forms) is a separate, read-only population: the
// system-generated satisfaction requests sent to a TICKET REQUESTER when
// this user (as the technician) completes their task -- see
// TicketFeedbackFormsTab.jsx's own header comment for why these must never
// be blended into Tab 1's counters or rows.
const TABS = [
  { key: "assigned", label: "Assigned Forms" },
  { key: "feedback", label: "Ticket Feedback Forms" },
];

export default function RoleReviewFormsPage() {
  const { user } = useContext(AuthContext) || {};
  // Staff's Review Forms table: Project/Module and Due are dropped (staff
  // assignments -- e.g. the auto-generated Technician Task Satisfaction
  // Form -- are never project-linked, and this list has no meaningful due
  // date for them either). Every other role reaching this shared page
  // (Technician, Developer/Data-Analyst, Manager, ...) keeps both columns
  // unchanged.
  const isStaff = user?.role === "staff";
  const [activeTab, setActiveTab] = useState("assigned");
  const {
    rows,
    total,
    loading,
    busyId,

    statusTab,
    setStatusTab,

    formTypeFilter,
    setFormTypeFilter,

    projectFilter,
    setProjectFilter,

    search,
    setSearch,

    formTypes,
    projects,

    currentPage,
    totalPages,
    setCurrentPage,

    hasActiveFilters,
    clearFilters,

    startOrContinue,
    viewSubmitted,
    fetchAssignments,
  } = useReviewForms();

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const visiblePages = getCompactPageRange(currentPage, totalPages).filter(
    (entry) => entry !== ELLIPSIS
  );


  return (
    <div className="tech-ds role-review-page">

      {/* ================= HEADER ================= */}
      <div className="tech-page-header">
        <div>
          <div className="tech-section-label">Form Management</div>

          <h2 className="tech-page-title">Assigned Forms</h2>

          <p className="tech-page-subtitle">
            Forms assigned to you for completion and review.
          </p>
        </div>

        <div className="tech-header-actions">
          {activeTab === "assigned" && (
            <button
              type="button"
              className="unit-btn unit-btn-light puws-btn puws-btn-ghost"
              onClick={fetchAssignments}
              disabled={loading}
            >
              <FaSyncAlt aria-hidden="true" /> {loading ? "Refreshing..." : "Refresh"}
            </button>
          )}
        </div>
      </div>

      {/* ================= TABS ================= */}
      <div className="rrf-tabs" role="tablist" aria-label="Forms">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.key}
            className={`rrf-tab${activeTab === tab.key ? " rrf-tab--active" : ""}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === "feedback" ? (
        <TicketFeedbackFormsTab />
      ) : (
        <>
      {/* ================= SUMMARY ================= */}
      <FormAssignmentSummaryCards
        variant="tech"
        primaryKeys={[
          "assignee.reviewFormsPending",
          "assignee.reviewFormsInProgress",
          "assignee.reviewFormsSubmitted",
          "assignee.reviewFormsOverdue",
        ]}
      />

      {/* ================= MAIN TABLE CARD ================= */}
      <section className="tech-card rrf-list-card">
        <div className="tech-toolbar">
          <div className="tech-toolbar-title">
            <h5>Assigned Forms</h5>
            <p>Search and manage forms assigned to you.</p>
          </div>

          <div className="tech-filters">
            <div className="tech-search">
              <span className="tech-search-ico"><FaSearch aria-hidden="true" /></span>

              <input
                type="text"
                placeholder="Search form, assignment..."
                aria-label="Search assigned forms"
                value={search}
                onChange={(e) => {
                  setCurrentPage(1);
                  setSearch(e.target.value);
                }}
              />
            </div>

            <select
              className="tech-select"
              aria-label="Filter by status"
              value={statusTab}
              onChange={(e) => {
                setCurrentPage(1);
                setStatusTab(e.target.value);
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
              <option value="overdue">Overdue</option>
            </select>

            <select
              className="tech-select tech-select--wide"
              aria-label="Filter by form type"
              value={formTypeFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setFormTypeFilter(e.target.value);
              }}
            >
              <option value="">All Form Types</option>

              {formTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>

            <select
              className="tech-select tech-select--wide"
              aria-label="Filter by project"
              value={projectFilter}
              onChange={(e) => {
                setCurrentPage(1);
                setProjectFilter(e.target.value);
              }}
            >
              <option value="">All Projects</option>

              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <button
                type="button"
                className="tech-reset-btn"
                onClick={clearFilters}
                aria-label="Clear filters"
                title="Clear filters"
              >
                <FaTimes aria-hidden="true" />
                <span className="tech-reset-label">Clear</span>
              </button>
            )}
          </div>
        </div>

        {/* ================= TABLE ================= */}
        <div className="tech-table-wrap">
          {loading ? (
            <div className="rrf-loading">
              <Spinner animation="border" size="sm" />
              <span>Loading assigned forms...</span>
            </div>
          ) : rows.length === 0 ? (
            <div className="rrf-empty">
              <div className="rrf-empty-icon"><FaClipboardList aria-hidden="true" /></div>

              <h5>No assigned forms found</h5>

              <p>
                {hasActiveFilters || statusTab
                  ? "Try another filter or clear the current filters."
                  : "You currently have no forms assigned."}
              </p>
            </div>
          ) : (
            <table className={`tech-table tech-table--forms${isStaff ? " tech-table--forms-staff" : ""}`}>
              <colgroup>
                <col className="rf-col-sno" />
                <col className="rf-col-form" />
                <col className="rf-col-assignment" />
                <col className="rf-col-creator" />
                {!isStaff && <col className="rf-col-project" />}
                {!isStaff && <col className="rf-col-due" />}
                <col className="rf-col-status" />
                <col className="rf-col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th className="tech-th-center">S.No</th>
                  <th>Form</th>
                  <th>Assignment</th>
                  <th>Created By</th>
                  {!isStaff && <th>Project / Module</th>}
                  {!isStaff && <th>Due</th>}
                  <th>Status</th>
                  <th className="tech-th-center">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((row, index) => {
                  const info = getReviewStatusInfo(row);
                  const action = getRecipientAction(row);

                  return (
                    <tr key={row.assignment_user_id}>
                      <td className="tech-td-center">
                        <span className="tech-sno">{index + 1}</span>
                      </td>

                      {/* FORM */}
                      <td>
                        <div className="tech-primary-cell">
                          <div className="tech-avatar" aria-hidden="true">
                            {row.form_name
                              ? row.form_name.charAt(0).toUpperCase()
                              : "F"}
                          </div>

                          <div className="tech-primary-text">
                            <strong className="tech-primary" title={row.form_name}>
                              {row.form_name || "—"}
                            </strong>

                            {row.form_type_name && (
                              <span className="tech-secondary" title={row.form_type_name}>
                                {row.form_type_name}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* ASSIGNMENT -- for a system-generated task-feedback
                          assignment (e.g. "Feedback: Task #415"), the
                          actual task's own title/category is far more
                          useful than that placeholder string + generic
                          instructions text. A regular, human-created
                          assignment (no related task) is unchanged. */}
                      {(() => {
                        const displayTitle = row.related_task_title || row.assignment_title || "—";
                        const categoryLabel = row.related_task_title
                          ? [row.related_task_category_name, row.related_task_sub_category_name]
                              .filter(Boolean)
                              .join(" / ")
                          : "";

                        return (
                          <td>
                            <div className="tech-primary-text rrf-assignment-cell">
                              <strong className="tech-primary" title={displayTitle}>{displayTitle}</strong>

                              {row.is_mandatory ? (
                                <span className="rrf-badge rrf-badge-mandatory">
                                  Mandatory
                                </span>
                              ) : null}

                              {categoryLabel ? (
                                <span className="tech-secondary" title={categoryLabel}>
                                  {categoryLabel}
                                </span>
                              ) : (
                                row.instructions && (
                                  <span
                                    className="tech-secondary"
                                    title={row.instructions}
                                  >
                                    {row.instructions}
                                  </span>
                                )
                              )}
                            </div>
                          </td>
                        );
                      })()}

                      {/* ASSIGNED BY */}
                      <td>
                        <span
                          className="rrf-person"
                          title={row.assigned_by_name || ""}
                        >
                          {row.assigned_by_name || "—"}
                        </span>
                      </td>

                      {/* PROJECT / MODULE */}
                      {!isStaff && (
                        <td>
                          <div className="tech-primary-text">
                            <strong className="tech-primary" title={row.project_name || ""}>
                              {row.project_name || "—"}
                            </strong>

                            {row.project_module_name && (
                              <span className="tech-secondary" title={row.project_module_name}>
                                {row.project_module_name}
                              </span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* DUE DATE */}
                      {!isStaff && (
                        <td>
                          <div className="rrf-due-cell">
                            <span
                              className={
                                row.is_overdue
                                  ? "rrf-due rrf-due-overdue"
                                  : "rrf-due"
                              }
                            >
                              {formatDate(row.effective_due_at)}
                            </span>
                            {row.is_overdue && (
                              <span className="rrf-overdue-chip">Overdue</span>
                            )}
                          </div>
                        </td>
                      )}

                      {/* STATUS */}
                      <td>
                        {(() => {
                          const statusTone =
                            info.tone === "light" ? "secondary" : info.tone;
                          const StatusIcon = STATUS_ICONS[statusTone] || FaClock;
                          return (
                            <span className={`rrf-status rrf-status-${statusTone}`}>
                              <StatusIcon className="rrf-status-icon" aria-hidden="true" />
                              {info.label}
                            </span>
                          );
                        })()}
                      </td>

                      {/* ACTION */}
                      <td>
                        <div className="rrf-actions">
                          {(action.type === "start" || action.type === "continue") && (
                            <button
                              type="button"
                              className={`rrf-action-primary${action.needsChanges ? " rrf-action-needs-changes" : ""}`}
                              disabled={
                                busyId === row.assignment_user_id
                              }
                              onClick={() => startOrContinue(row)}
                            >
                              {busyId === row.assignment_user_id ? (
                                "Please wait..."
                              ) : (
                                <>
                                  <FaPlay
                                    className="rrf-action-primary-icon"
                                    aria-hidden="true"
                                  />
                                  {action.needsChanges ? "Correct Response" : action.type === "continue" ? "Continue" : "Start"}
                                </>
                              )}
                            </button>
                          )}

                          {action.type === "view" && (
                            <button
                              type="button"
                              className="rrf-action-icon"
                              title="View submission"
                              aria-label="View submission"
                              onClick={() => viewSubmitted(row)}
                            >
                              <FaEye aria-hidden="true" />
                            </button>
                          )}

                          {action.type === "none" && (
                            <span className="rrf-action-empty">
                              {RECIPIENT_ACTION_REASON_LABELS[action.reason] || "—"}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ================= FOOTER ================= */}
        {!loading && rows.length > 0 && (
          <div className="tech-footer">
            <div className="rrf-record-info">
              Showing{" "}
              <strong>{rows.length}</strong>{" "}
              of{" "}
              <strong>{total}</strong>{" "}
              assigned form(s)
            </div>

            {totalPages > 1 && (
              <div className="tech-pagination">
                <button
                  type="button"
                  className="puws-page-btn"
                  aria-label="Previous page"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((page) => page - 1)
                  }
                >
                  ‹
                </button>

                {visiblePages.map((page, index) => {
                  const previousPage =
                    visiblePages[index - 1];

                  const showGap =
                    previousPage &&
                    page - previousPage > 1;

                  return (
                    <React.Fragment key={page}>
                      {showGap && (
                        <span className="puws-page-btn puws-page-gap">
                          …
                        </span>
                      )}

                      <button
                        type="button"
                        aria-current={page === currentPage ? "page" : undefined}
                        className={
                          page === currentPage
                            ? "puws-page-btn active"
                            : "puws-page-btn"
                        }
                        onClick={() =>
                          setCurrentPage(page)
                        }
                      >
                        {page}
                      </button>
                    </React.Fragment>
                  );
                })}

                <button
                  type="button"
                  className="puws-page-btn"
                  aria-label="Next page"
                  disabled={currentPage === totalPages}
                  onClick={() =>
                    setCurrentPage((page) => page + 1)
                  }
                >
                  ›
                </button>
              </div>
            )}
          </div>
        )}
      </section>
        </>
      )}
    </div>
  );
}